import axios from "axios";

// Resolve backend base at runtime with safe fallback:
// * If REACT_APP_BACKEND_URL is present at build time → "<url>/api"
// * Otherwise (undefined/empty) → relative "/api", so nginx on the same
//   domain proxies /api/* to the backend (see DEPLOY.md). This avoids the
//   "undefined/api/leads → 405 Not Allowed" bug when the frontend is built
//   without the env var set.
const rawBase = (process.env.REACT_APP_BACKEND_URL || "")
    .replace(/\/+$/, "")
    .trim();
const isBadBase = !rawBase || rawBase === "undefined" || rawBase === "null";
const API = isBadBase ? "/api" : `${rawBase}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

const TOKEN_KEY = "sa_admin_token";

export const setToken = (token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);

// Inject Authorization header on every request when a token exists
api.interceptors.request.use((config) => {
    const t = getToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

// On 401, clear token so the UI can redirect to /admin/login
// Also guard against HTML fallbacks (e.g. SPA index.html served when the
// backend URL is misconfigured on deploy) — otherwise callers receive a
// string and .map() crashes with "o.map is not a function".
api.interceptors.response.use(
    (r) => {
        const ct = String(r?.headers?.["content-type"] || "").toLowerCase();
        const looksHtml =
            ct.includes("text/html") ||
            (typeof r?.data === "string" && r.data.trim().startsWith("<"));
        if (looksHtml) {
            const err = new Error(
                "Backend returned HTML instead of JSON — check REACT_APP_BACKEND_URL",
            );
            err.response = r;
            return Promise.reject(err);
        }
        return r;
    },
    (err) => {
        if (err?.response?.status === 401) {
            setToken(null);
        }
        return Promise.reject(err);
    }
);

// Small helper — always yields an array, so map()/length are safe even when
// the API misbehaves in production (returns null, an error object, HTML, etc).
export const asArray = (v) => (Array.isArray(v) ? v : []);

export const API_URL = API;
